import { Button, Drawer, Modal, Form, Input, Select, Row, Col } from 'antd';
import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { templates } from '../../utilities/objects/template';
import SEOHead from '../../components/common/SEOHead';
import JsonLd from '../../components/common/JsonLd';
import RunPanda from "../../assets/images/Panda/red-panda-mascot-ready-to-action.png";
import PandaCart from "../../assets/images/red-panda.png";
import { TemplateFeature, SupportService } from '../../models';
import ResponsiveImage from '../../components/common/ResponsiveImage';


const Detail: React.FC = () => {
    const navigate = useNavigate();
    const [selectedServices, setSelectedServices] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState('content');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [orderModalOpen, setOrderModalOpen] = useState(false);

    const [form] = Form.useForm();
    const paymentSectionRef = useRef<HTMLDivElement>(null);

    const { id } = useParams<{ id: string }>();
    const templateId = id ? parseInt(id, 10) : 1;
    const template = templates.find(t => t.id === templateId) || templates[0];

    // Mock data - thực tế sẽ lấy từ API
    const templateData = {
        id: String(template.id),
        name: template.name,
        category: ['food', 'hotel', 'edu', 'bds'].includes(template.category) ? template.category : 'food',
        price: template.price || 2500000,
        originalPrice: (template.price || 2500000) + 1000000,
        description: template.title || `${template.name} - Mẫu giao diện website chuyên nghiệp tối ưu SEO cho doanh nghiệp.`,
        images: template.category === 'hotel' ? '/Template/Food/food1/index.html' : `/Template/Food/food${((template.id - 1) % 3) + 1}/index.html`,
        demoUrl: template.category === 'hotel' ? '/Template/Food/food1/index.html' : `/Template/Food/food${((template.id - 1) % 3) + 1}/index.html`,
        technologies: ['HTML5', 'CSS3', 'JavaScript', 'Bootstrap', 'jQuery'],
        responsive: true,
        seoOptimized: true,
    };

    const features: TemplateFeature[] = [
        { id: '1', name: 'Trang chủ', description: 'Trang chủ với slider và giới thiệu', included: true },
        { id: '2', name: 'Menu Online', description: 'Danh sách món ăn với hình ảnh', included: true },
        { id: '3', name: 'Đặt bàn', description: 'Form đặt bàn trực tuyến', included: true },
        { id: '4', name: 'Liên hệ', description: 'Trang liên hệ với bản đồ', included: true },
        { id: '5', name: 'Blog', description: 'Trang blog tin tức', included: false, price: 500000 },
        { id: '6', name: 'Đa ngôn ngữ', description: 'Hỗ trợ tiếng Việt và tiếng Anh', included: false, price: 800000 },
        { id: '7', name: 'Thanh toán online', description: 'Tích hợp cổng thanh toán', included: false, price: 1200000 },
        { id: '8', name: 'Khác', description: 'Tích hợp cổng thanh toán', included: false, price: 0 },

    ];

    const supportServices: SupportService[] = [
        {
            id: 'setup',
            name: 'Cài đặt website',
            description: 'Cài đặt template lên hosting, cấu hình cơ bản',
            price: 300000,
            duration: '1-2 ngày',
        },
        {
            id: 'customization',
            name: 'Tùy chỉnh giao diện',
            description: 'Thay đổi màu sắc, logo, hình ảnh theo yêu cầu',
            price: 800000,
            duration: '3-5 ngày',
            popular: true,
        },
        {
            id: 'content',
            name: 'Nhập liệu nội dung',
            description: 'Nhập nội dung, hình ảnh, menu theo yêu cầu',
            price: 500000,
            duration: '2-3 ngày',
        },
        {
            id: 'seo',
            name: 'Tối ưu SEO',
            description: 'Tối ưu SEO cơ bản, sitemap, meta tags',
            price: 600000,
            duration: '1-2 ngày',
        },
        {
            id: 'training',
            name: 'Hướng dẫn sử dụng',
            description: 'Hướng dẫn quản trị và cập nhật nội dung',
            price: 400000,
            duration: '1 ngày',
        },
        {
            id: 'maintenance',
            name: 'Bảo trì 6 tháng',
            description: 'Hỗ trợ kỹ thuật và bảo trì trong 6 tháng',
            price: 1500000,
            duration: '6 tháng',
        },
    ];

    // Tab data for content services
    const contentServiceTabs = {
        content: {
            name: 'Viết content',
            description: 'Lên nội dung cho toàn bộ website của bạn theo phong cách chuyên nghiệp, tạo cảm giác tin tưởng cao cho người dùng khi đến với website của bạn.',
            services: [
                { id: 'content-100', name: 'Nhập liệu 100 bài', price: 2000000, selected: false },
                { id: 'content-500', name: 'Nhập liệu 500 bài', price: 9000000, selected: false },
                { id: 'content-1000', name: 'Nhập liệu 1000 bài', price: 16000000, selected: false },
                { id: 'content-2000', name: 'Nhập liệu 2000 bài', price: 28000000, selected: false },
                { id: 'content-3000', name: 'Nhập liệu 3000 bài', price: 36000000, selected: false },
                { id: 'import-articles', name: 'Import bài viết', price: 5000000, selected: false, info: true },
                { id: 'import-products', name: 'Import sản phẩm', price: 8000000, selected: false, info: true },
            ]
        },
        marketing: {
            name: 'Chiến dịch MKT Online',
            description: 'Xây dựng chiến dịch marketing online hiệu quả để tăng lưu lượng truy cập và chuyển đổi khách hàng.',
            services: [
                { id: 'seo-basic', name: 'SEO cơ bản', price: 3000000, selected: false },
                { id: 'google-ads', name: 'Chạy Google Ads', price: 5000000, selected: false },
                { id: 'facebook-ads', name: 'Chạy Facebook Ads', price: 4000000, selected: false },
                { id: 'social-media', name: 'Quản lý Social Media', price: 6000000, selected: false },
            ]
        },
        hosting: {
            name: 'Hosting và tên miền',
            description: 'Cung cấp hosting chất lượng cao và đăng ký tên miền phù hợp cho website của bạn.',
            services: [
                { id: 'hosting-basic', name: 'Hosting cơ bản', price: 1200000, selected: false },
                { id: 'hosting-premium', name: 'Hosting cao cấp', price: 2400000, selected: false },
                { id: 'domain-com', name: 'Tên miền .com', price: 400000, selected: false },
                { id: 'ssl-certificate', name: 'Chứng chỉ SSL', price: 800000, selected: false },
            ]
        },
        payment: {
            name: 'Thanh toán',
            description: 'Tích hợp các cổng thanh toán phổ biến để khách hàng có thể thanh toán dễ dàng.',
            services: [
                { id: 'vnpay', name: 'Tích hợp VNPay', price: 2000000, selected: false },
                { id: 'momo', name: 'Tích hợp MoMo', price: 1500000, selected: false },
                { id: 'paypal', name: 'Tích hợp PayPal', price: 2500000, selected: false },
            ]
        },
        support: {
            name: 'After sale',
            description: 'Dịch vụ hỗ trợ sau bán hàng với đội ngũ kỹ thuật chuyên nghiệp.',
            services: [
                { id: 'support-3m', name: 'Hỗ trợ 3 tháng', price: 1500000, selected: false },
                { id: 'support-6m', name: 'Hỗ trợ 6 tháng', price: 2800000, selected: false },
                { id: 'support-12m', name: 'Hỗ trợ 12 tháng', price: 5000000, selected: false },
            ]
        }
    };

    const handleServiceToggle = (serviceId: string) => {
        setSelectedServices(prev =>
            prev.includes(serviceId)
                ? prev.filter(id => id !== serviceId)
                : [...prev, serviceId]
        );
    };

    const scrollToPayment = () => {
        paymentSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    };

    const calculateTotal = () => {
        const basePrice = templateData.price;
        const featuresPrice = features
            .filter(f => !f.included && selectedServices.includes(f.id))
            .reduce((sum, f) => sum + (f.price || 0), 0);
        const servicesPrice = supportServices
            .filter(s => selectedServices.includes(s.id))
            .reduce((sum, s) => sum + s.price, 0);

        return basePrice + featuresPrice + servicesPrice;
    };

    // Count selected items
    const getSelectedItemsCount = () => {
        return selectedServices.length;
    };

    const handleOrderSubmit = (values: any) => {
        console.log('Order submitted:', {
            ...values,
            templateId: templateData.id,
            selectedServices,
            totalAmount: calculateTotal(),
        });
        // Xử lý đặt hàng ở đây
        Modal.success({
            title: 'Đặt hàng thành công!',
            content: 'Chúng tôi sẽ liên hệ với bạn trong thời gian sớm nhất.',
        });
        setOrderModalOpen(false);
        form.resetFields();
    };

    return (
        <div className="min-h-screen">
            <SEOHead
                title={`${template.name} — TrioCore Solutions`}
                description={template.title || `${template.name} - Mẫu giao diện website chuyên nghiệp tối ưu SEO cho doanh nghiệp.`}
                ogImage={template.img}
                path={`/store/detail/${template.id}`}
            />
            <JsonLd
                data={{
                    "@context": "https://schema.org",
                    "@type": "Product",
                    "name": template.name,
                    "image": "https://triocoresolutions.com.vn" + template.img,
                    "description": template.title || template.name,
                    "offers": {
                        "@type": "Offer",
                        "priceCurrency": "VND",
                        "price": template.price || 2500000,
                        "availability": "https://schema.org/InStock"
                    }
                }}
            />
            <JsonLd
                data={{
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Trang chủ",
                            "item": "https://triocoresolutions.com.vn/"
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "Kho giao diện",
                            "item": "https://triocoresolutions.com.vn/store"
                        },
                        {
                            "@type": "ListItem",
                            "position": 3,
                            "name": template.name,
                            "item": `https://triocoresolutions.com.vn/store/detail/${template.id}`
                        }
                    ]
                }}
            />
            {/* Fixed Bear Cart Icon */}

            <div className="fixed top-[calc(76%)] right-5 z-50 transform -translate-y-1/2 bg-white rounded-full box-shadow-main">
                <div
                    onClick={() => setDrawerOpen(true)}
                    className="relative cursor-pointer rounded-full p-3  hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                    <ResponsiveImage
                        src={PandaCart}
                        alt="Cart"
                        className="w-[70px] h-auto object-cover rounded-full scale-110"
                    />
                    {getSelectedItemsCount() > 0 && (
                        <span className="absolute top-0 right-1 bg-[var(--textBlue57)] text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                            {getSelectedItemsCount()}
                        </span>
                    )}
                </div>
            </div>

            {/* Order Summary Drawer — conditional mount to prevent antd CSS-in-JS leak during react-snap pre-render */}
            {drawerOpen && (
            <Drawer
                title="Tổng kết đơn hàng"
                placement="right"
                onClose={() => setDrawerOpen(false)}
                open={drawerOpen}
                width={400}
            >
                <div className="flex flex-col h-full justify-between">
                    <div className="space-y-2 h-[400px] overflow-y-auto">
                        <div className="flex justify-between font-medium">
                            <span>Template gốc:</span>
                            <span>{templateData.price.toLocaleString()}₫</span>
                        </div>

                        {features.filter(f => !f.included && selectedServices.includes(f.id)).map(feature => (
                            <div key={feature.id} className="flex justify-between text-sm text-gray-600">
                                <span>+ {feature.name}:</span>
                                <span>+{feature.price?.toLocaleString()}₫</span>
                            </div>
                        ))}

                        {supportServices.filter(s => selectedServices.includes(s.id)).map(service => (
                            <div key={service.id} className="flex justify-between text-sm text-gray-600">
                                <span>+ {service.name}:</span>
                                <span>+{service.price.toLocaleString()}₫</span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-20">
                        <div className="border-t pt-4 ">
                            <div className="flex justify-between items-center text-xl font-bold">
                                <span>Tổng cộng:</span>
                                <span className="text-blue-500">{calculateTotal().toLocaleString()}₫</span>
                            </div>
                        </div>

                        <div className="space-y-3 mt-6">
                            <button
                                onClick={() => {
                                    navigate(`/preview/${templateData.category + "-" + templateData.id}`);
                                    setDrawerOpen(false);
                                }}
                                className="w-full button-secondary px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Xem thử trước
                            </button>
                            <button className="button-primary w-full px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                onClick={() => setOrderModalOpen(true)}>
                                Đặt hàng ngay
                            </button>
                        </div>
                    </div>
                </div>
            </Drawer>
            )}

            {/* Header */}
            <div className='container'>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Hình ảnh giao diện */}
                        <div className="space-y-4">
                            <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                                <div className="aspect-video bg-gray-200">
                                    {navigator.userAgent !== "ReactSnap" ? (
                                        <iframe
                                            src={templateData.images}
                                            className="w-full h-full"
                                            title="Template Preview"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                                            Template Preview (Prerendering)
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Thông tin template */}
                        <div className="space-y-6">
                            <div className="bg-white rounded-lg p-6 shadow-lg">
                                <div className='flex items-center justify-between'>
                                    <h1 className="text-1xl font-bold text-gray-600 mb-2">{templateData.name}</h1>
                                    <div>
                                        <span className="text-2xl font-bold text-[var(--textBlue57)]">
                                            {templateData.price.toLocaleString()}₫
                                        </span>
                                        <span className="text-[16px] text-gray-500 line-through ml-2">
                                            {templateData.originalPrice.toLocaleString()}₫
                                        </span>
                                    </div>
                                </div>

                                <p className="h-[50px] text-gray-500 text-[16px] text-left mb-5 mt-5">{templateData.description}</p>

                                {/* Thống kê và badge */}
                                <div className="border-t pt-4 mt-9">
                                    <div className="grid grid-cols-3 gap-4 text-center">
                                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg">
                                            <div className="text-2xl font-bold text-blue-600">99%</div>
                                            <div className="text-xs text-blue-700">hài lòng</div>
                                        </div>
                                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg">
                                            <div className="text-2xl font-bold text-blue-600">7 ngày</div>
                                            <div className="text-xs text-blue-700">Giao hàng</div>
                                        </div>
                                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg">
                                            <div className="text-2xl font-bold text-blue-600">1 năm</div>
                                            <div className="text-xs text-blue-700">Bảo hành</div>
                                        </div>
                                    </div>
                                </div>

                                <div className='mt-6'>
                                    <button type="button" onClick={scrollToPayment} className='ml-auto button-secondary'>
                                        <span className='text-[12px]'>Thanh Toán</span>
                                    </button>
                                    <button type="button" onClick={() => navigate(`/preview/${templateData.category + "-" + templateData.id}`)} className='ml-3 button-secondary'>
                                        <span className='text-[12px]'>Xem Demo</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Order Modal — conditional mount to prevent antd CSS-in-JS leak during react-snap pre-render */}
                        {orderModalOpen && (
                        <Modal
                            title={
                                <div className="flex items-center">
                                    <div className="p-2 bg-[var(--textBlue58)] rounded-lg mr-3">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                        </svg>
                                    </div>
                                    <span className="text-xl font-bold text-gray-600">Đặt hàng Website</span>
                                </div>
                            }
                            style={{ top: 40 }} // số càng nhỏ thì modal càng sát mép trên

                            open={orderModalOpen}
                            onCancel={() => setOrderModalOpen(false)}
                            footer={null}
                            width={700}
                            className="order-modal z-[100] mb-20 h-[auto]"
                        >
                            <div className="p-2 ">
                                {/* Order Summary */}
                                <div className="bg-blue-100 rounded-lg p-4 mb-6">
                                    <h4 className="font-semibold text-gray-800 mb-3">Thông tin đơn hàng</h4>
                                    <div className="space-y-2 text-sm max-h-[50px] overflow-auto ">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">{templateData.name}</span>
                                            <span className="font-semibold text-blue-600 mr-3">{templateData.price.toLocaleString()}₫</span>
                                        </div>
                                        {features.filter(f => !f.included && selectedServices.includes(f.id)).map(feature => (
                                            <div key={feature.id} className="flex justify-between text-gray-600">
                                                <span>+ {feature.name}</span>
                                                <span className='mr-3'>+{feature.price?.toLocaleString()}₫</span>
                                            </div>
                                        ))}
                                        {supportServices.filter(s => selectedServices.includes(s.id)).map(service => (
                                            <div key={service.id} className="flex justify-between text-gray-600">
                                                <span>+ {service.name}</span>
                                                <span className='mr-3'>+{service.price.toLocaleString()}₫</span>
                                            </div>
                                        ))}

                                    </div>
                                    <div className="border-t pt-2 mt-3">
                                        <div className="flex justify-between font-bold text-lg">
                                            <span>Tổng cộng:</span>
                                            <span className="text-blue-600">{calculateTotal().toLocaleString()}₫</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Order Form */}
                                <Form
                                    form={form}
                                    layout="vertical"
                                    onFinish={handleOrderSubmit}
                                >
                                    <Row gutter={24} className='max-h-[350px] overflow-auto'>
                                        <Col span={12} className="pr-2">
                                            <Form.Item
                                                label="Họ và tên"
                                                name="fullName"
                                                rules={[{ required: true, message: 'Vui lòng nhập họ tên!' }]}
                                            >
                                                <Input size="middle" placeholder="Nhập họ và tên của bạn" />
                                            </Form.Item>
                                        </Col>

                                        <Col span={12} className="pl-2">
                                            <Form.Item
                                                label="Số điện thoại"
                                                name="phone"
                                                rules={[
                                                    { required: true, message: 'Vui lòng nhập số điện thoại!' },
                                                    { pattern: /^[0-9]{10,11}$/, message: 'Số điện thoại không hợp lệ!' }
                                                ]}
                                            >
                                                <Input size="middle" placeholder="Nhập số điện thoại" />
                                            </Form.Item>
                                        </Col >
                                        <Col span={24} >
                                            <Form.Item
                                                required
                                                label="Email"
                                                name="company"

                                            >
                                                <Input size="middle" type='Email' placeholder="Nhập tên công ty (không bắt buộc)" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Form.Item
                                                label="Ngành nghề kinh doanh"
                                                name="industry"
                                                rules={[{ required: true, message: 'Vui lòng chọn ngành nghề!' }]}
                                            >
                                                <Select size="middle" placeholder="Chọn ngành nghề kinh doanh">
                                                    <Select.Option value="restaurant">Nhà hàng/Ăn uống</Select.Option>
                                                    <Select.Option value="retail">Bán lẻ</Select.Option>
                                                    <Select.Option value="services">Dịch vụ</Select.Option>
                                                    <Select.Option value="healthcare">Y tế</Select.Option>
                                                    <Select.Option value="education">Giáo dục</Select.Option>
                                                    <Select.Option value="technology">Công nghệ</Select.Option>
                                                    <Select.Option value="real-estate">Bất động sản</Select.Option>
                                                    <Select.Option value="other">Khác</Select.Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>


                                        <Col span={24}>
                                            <Form.Item
                                                label="Ghi chú thêm"
                                                name="notes"
                                            >
                                                <Input.TextArea
                                                    rows={4}
                                                    placeholder="Nếu có yêu cầu đặc biệt, vui lòng ghi rõ tại đây..."
                                                />
                                            </Form.Item>

                                        </Col>
                                    </Row>


                                    <div className="flex gap-3 pt-5">
                                        <Button
                                            size="middle"
                                            onClick={() => setOrderModalOpen(false)}
                                            className="flex-1"
                                        >
                                            Hủy bỏ
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="middle"
                                            htmlType="submit"
                                            className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 border-none"
                                        >
                                            Xác nhận đặt hàng
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </Modal>
                        )}

                        {/* Custom Contact Modal - Full width support */}
            
                    </div>                    {/* Chức năng cơ bản */}
                    <div className="mt-20">
                        <h2 className="text-3xl font-bold text-[var(--textBlue57)]">Tính năng cơ bản</h2>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-7">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 text-center">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Chức năng</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Mô tả</th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Trạng thái</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Giá thêm</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200 ">
                                        {features.map((feature) => (
                                            <tr key={feature.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {feature.name}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-700">
                                                    {feature.description}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    {feature.included ? (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[var(--textBlue58)] text-white">
                                                            Đã bao gồm
                                                        </span>
                                                    ) : (
                                                        <label className="inline-flex items-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedServices.includes(feature.id)}
                                                                onChange={() => handleServiceToggle(feature.id)}
                                                                className="rounded border-gray-300 text-red-600 shadow-sm focus:border-red-300 focus:ring focus:ring-red-200 focus:ring-opacity-50"
                                                            />
                                                            <span className="ml-2 text-sm text-gray-600">Thêm</span>
                                                        </label>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                                                    {feature.included ? (
                                                        <span className="text-[var(--textBlue58)]">Miễn phí</span>
                                                    ) : (
                                                        <span className="font-medium">+{feature.price?.toLocaleString()}₫</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Dịch vụ hỗ trợ thêm */}
                    <div className="mt-20">
                        <h2 className="text-3xl font-bold text-[var(--textBlue57)] mb-6">Dịch vụ hỗ trợ thêm</h2>

                        {/* Tab Navigation */}
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-7">
                            <div className="flex overflow-x-auto">
                                {Object.entries(contentServiceTabs).map(([key, tab]) => (
                                    <button
                                        key={key}
                                        onClick={() => setActiveTab(key)}
                                        className={`
                                            relative px-6 py-4 text-sm font-medium whitespace-nowrap 
                                            transition-all duration-300 ease-in-out
                                            ${activeTab === key
                                                ? 'text-white bg-[var(--textBlue58)] border-b-2 border-blue-500 shadow-md transform scale-105'
                                                : 'text-gray-600 bg-white hover:bg-gray-50 hover:text-blue-500 border-b-2 border-transparent'
                                            }
                                            first:rounded-tl-lg last:rounded-tr-lg
                                            hover:shadow-lg
                                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
                                        `}
                                    >
                                        <span className="relative z-10">{tab.name}</span>
                                        {activeTab === key && (
                                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-t-lg" />
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Tab Content */}
                            <div className="p-6">
                                {Object.entries(contentServiceTabs).map(([key, tab]) => (
                                    activeTab === key && (
                                        <div key={key} className="space-y-6">
                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                {tab.description}
                                            </p>

                                            {activeTab === 'content' && (
                                                <div>
                                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Nhập liệu sản phẩm</h3>
                                                    <div className="space-y-3">
                                                        {tab.services.map((service) => (
                                                            <div key={service.id} className="flex items-center justify-between py-3 border-b border-gray-100">
                                                                <div className="flex items-center">
                                                                    <input
                                                                        type="radio"
                                                                        name="content-service"
                                                                        id={service.id}
                                                                        className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                                                                        onChange={() => handleServiceToggle(service.id)}
                                                                    />
                                                                    <label htmlFor={service.id} className="ml-3 text-sm text-gray-700 flex items-center">
                                                                        {service.name}
                                                                        {(service as any).info && (
                                                                            <div className="ml-2 w-4 h-4 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs">
                                                                                i
                                                                            </div>
                                                                        )}
                                                                    </label>
                                                                </div>
                                                                <span className="text-sm font-medium text-gray-900">
                                                                    {service.price.toLocaleString()}đ
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab !== 'content' && (
                                                <div className="grid gap-4">
                                                    {tab.services.map((service) => (
                                                        <div key={service.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors">
                                                            <div className="flex items-center">
                                                                <input
                                                                    type="checkbox"
                                                                    id={service.id}
                                                                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                                                    onChange={() => handleServiceToggle(service.id)}
                                                                />
                                                                <label htmlFor={service.id} className="ml-3 text-sm font-medium text-gray-700">
                                                                    {service.name}
                                                                </label>
                                                            </div>
                                                            <span className="text-sm font-semibold text-purple-600">
                                                                {service.price.toLocaleString()}đ
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Tổng kết đơn hàng */}
                    <div className='flex mt-16 items-stretch justify-center relative min-h-[500px] bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 rounded-3xl p-8'>
                        {/* Left side - Panda with dynamic sizing */}
                        <div className='w-2/5  flex items-center justify-center'>
                            <ResponsiveImage
                                src={RunPanda}
                                alt="Run Panda"
                                className='absolute w-[600px] -bottom-7 -left-20 animate-bounce-slow drop-shadow-xl mt-auto'
                            />
                            {/* Floating elements around panda - muted colors */}
                            <div className="absolute top-10 left-10 w-6 h-6 bg-blue-200 rounded-full animate-pulse opacity-40"></div>
                            <div className="absolute top-32 right-16 w-4 h-4 bg-indigo-200 rounded-full animate-ping opacity-30"></div>
                            <div className="absolute bottom-20 left-20 w-8 h-8 bg-gray-300 rounded-full animate-bounce opacity-30"></div>
                        </div>

                        {/* Right side - Enhanced order summary */}
                        <div className="w-3/5 relative flex items-center" ref={paymentSectionRef}>
                            <div className="w-full relative bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200 hover:shadow-2xl transition-all duration-300">
                                {/* Decorative elements - subtle */}
                                <div className="absolute -top-3 -right-3 w-6 h-6 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full opacity-20"></div>
                                <div className="absolute -bottom-3 -left-3 w-8 h-8 bg-gradient-to-br from-gray-400 to-blue-400 rounded-full opacity-15"></div>

                                {/* Header with icon */}
                                <div className="flex items-center mb-8">
                                    <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl mr-4 shadow-md">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-800">
                                        Tổng kết đơn hàng
                                    </h3>
                                </div>

                                {/* Order items with enhanced styling */}
                                <div className="space-y-3 mb-8 h-[200px] overflow-y-auto">
                                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                                        <div className="flex items-center">
                                            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                                            <span className="font-medium text-gray-700">Template gốc:</span>
                                        </div>
                                        <span className="font-bold text-blue-600">{templateData.price.toLocaleString()}₫</span>
                                    </div>

                                    {features.filter(f => !f.included && selectedServices.includes(f.id)).map((feature, index) => (
                                        <div key={feature.id} className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-l-4 border-green-400">
                                            <div className="flex items-center">
                                                <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                                                <span className="text-sm text-gray-600">+ {feature.name}:</span>
                                            </div>
                                            <span className="text-sm font-semibold text-green-600">+{feature.price?.toLocaleString()}₫</span>
                                        </div>
                                    ))}

                                    {supportServices.filter(s => selectedServices.includes(s.id)).map((service, index) => (
                                        <div key={service.id} className="flex justify-between items-center p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border-l-4 border-indigo-400">
                                            <div className="flex items-center">
                                                <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                                                <span className="text-sm text-gray-600">+ {service.name}:</span>
                                            </div>
                                            <span className="text-sm font-semibold text-indigo-600">+{service.price.toLocaleString()}₫</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Total section with enhanced design */}
                                <div className="relative p-4 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg mb-8">
                                    <div className="relative flex justify-between items-center">
                                        <div className="flex items-center">
                                            <div className="p-2 bg-white/20 rounded-lg mr-3">
                                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                                </svg>
                                            </div>
                                            <span className="text-xl font-bold text-white">Tổng cộng:</span>
                                        </div>
                                        <span className="text-2xl font-bold text-white drop-shadow-lg">
                                            {calculateTotal().toLocaleString()}₫
                                        </span>
                                    </div>
                                </div>

                                {/* Action buttons with enhanced styling */}
                                <div className="flex gap-4">
                                    <button
                                        onClick={() => navigate(`/preview/${templateData.category + "-" + templateData.id}`)}
                                        className="flex-1 group relative overflow-hidden bg-white border-2 border-gray-300 rounded-xl px-6 py-4 text-gray-700 font-semibold transition-all duration-300 hover:border-blue-400 hover:shadow-lg"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        <div className="relative flex items-center justify-center">
                                            <svg className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            Xem thử trước
                                        </div>
                                    </button>

                                    <button className="flex-1 group relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl px-6 py-4 text-white font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                                        onClick={() => setOrderModalOpen(true)}>
                                        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        <div className="relative flex items-center justify-center">
                                            <svg className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                            Đặt hàng ngay
                                        </div>
                                    </button>
                                </div>

                                {/* Trust badges */}
                                <div className="flex items-center justify-center mt-6 space-x-6 pt-4 border-t border-gray-200">
                                    <div className="flex items-center text-xs text-gray-500">
                                        <svg className="w-4 h-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Bảo mật
                                    </div>
                                    <div className="flex items-center text-xs text-gray-500">
                                        <svg className="w-4 h-4 text-blue-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Uy tín
                                    </div>
                                    <div className="flex items-center text-xs text-gray-500">
                                        <svg className="w-4 h-4 text-purple-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Chất lượng
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Detail;
